import { createTheme } from "@mui/material/styles";
import type { CSSProperties } from "react";
import { PRIMARY_COLOR, TEXT_PRIMARY, TEXT_SECONDARY } from "./consts";

declare module "@mui/material/styles" {
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    "2xl": true;
  }
  interface Palette {
    neutral: Palette["primary"];
  }
  interface PaletteOptions {
    neutral?: PaletteOptions["primary"];
  }
  interface TypographyVariants {
    journeyTileMeta: CSSProperties;
    journeyTileTitle: CSSProperties;
    journeyTileBody: CSSProperties;
  }
  interface TypographyVariantsOptions {
    journeyTileMeta?: CSSProperties;
    journeyTileTitle?: CSSProperties;
    journeyTileBody?: CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    journeyTileMeta: true;
    journeyTileTitle: true;
    journeyTileBody: true;
  }
}

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: PRIMARY_COLOR,
      light: "#C42040",
      dark: "#7A0419",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#38b2ac",
      light: "#4fd1c5",
      dark: "#2c9690",
      contrastText: "#ffffff",
    },
    neutral: {
      main: "#64748b",
      light: "#94a3b8",
      dark: "#475569",
      contrastText: "#ffffff",
    },
    background: {
      default: "#f8fafc",
      paper: "#ffffff",
    },
    text: {
      primary: TEXT_PRIMARY,
      secondary: TEXT_SECONDARY,
    },
  },
  typography: {
    fontFamily: '"Barlow", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: "2.5rem",
      fontWeight: 700,
      lineHeight: 1.25,
    },
    h2: {
      fontSize: "1.5rem", // xs - default
      fontWeight: 500,
      lineHeight: 1.25,
    },
    h3: {
      fontSize: "1.5rem",
      fontWeight: 600,
      lineHeight: 1.25,
    },
    h4: {
      fontSize: "1.25rem",
      fontWeight: 600,
      lineHeight: 1.25,
    },
    h5: {
      fontSize: "1rem",
      fontWeight: 600,
      lineHeight: 1.25,
    },
    h6: {
      fontSize: "0.875rem",
      fontWeight: 600,
      lineHeight: 1.25,
    },
    body1: {
      fontSize: "22px",
      fontWeight: 300,
      fontStyle: "light",
      lineHeight: 1.25,
      color: TEXT_PRIMARY,
    },
    body2: {
      fontSize: "1rem",
      fontWeight: 300,
      fontStyle: "light",
      lineHeight: 1.25,
      color: TEXT_SECONDARY,
    },
    journeyTileMeta: {
      fontSize: "1rem",
      fontWeight: 300,
      lineHeight: 1,
      letterSpacing: 0,
      textTransform: "uppercase",
    },
    journeyTileTitle: {
      fontSize: "1.375rem",
      fontWeight: 500,
      lineHeight: 1,
      letterSpacing: 0,
      textTransform: "uppercase",
    },
    journeyTileBody: {
      fontSize: "1rem",
      fontWeight: 300,
      lineHeight: 1,
      letterSpacing: 0,
    },
    button: {
      textTransform: "none",
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1521,
      "2xl": 1921,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "0.5rem",
          padding: "0.5rem 1.5rem",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "0.75rem",
          boxShadow:
            "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          // Scroll snap disabled on mobile (xs) to prevent jumping with address bar
          scrollSnapType: "none",
          "@media (min-width: 900px)": {
            scrollSnapType: "y proximity",
          },
        },
        html: {
          scrollSnapType: "none",
          "@media (min-width: 900px)": {
            scrollSnapType: "y proximity",
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h2: ({ theme }) => ({
          fontSize: "2rem", // xs
          [theme.breakpoints.up("md")]: {
            fontSize: "2.5rem", // md
          },
        }),
      },
    },
  },
});

export default theme;
